/* eslint-disable react/no-danger */
/* eslint-disable jam3/no-sanitizer-with-danger */
import { Button, Card, Paragraph } from 'elmo-elements';
import React, { ReactElement } from 'react';
import './QuizResult.scss';
import DOMPurify from 'dompurify';
import { FailBehaviour } from '@/modules/types/choice';
import {
  getTimeDifference,
  processRetryTimeInSeconds,
  processRetryTimeMessage,
} from '@/components/Player/FrameContent/util';
import { QuizAttemptResponseType, QuizMetadata } from '@/modules/types/quiz';

const QuizResult = ({
  score,
  pass,
  handler,
  content,
  quizMetadata,
  quizAttemptResponse,
}: {
  score: number;
  pass: number;
  handler: {
    tryAgainSubmitHandler: () => void;
    restartSubmitHandler: () => void;
  };
  content?: {
    passContent?: string;
    failContent?: string;
    retryContent?: string;
  };
  quizMetadata: QuizMetadata;
  quizAttemptResponse: QuizAttemptResponseType | null | undefined;
}): ReactElement => {
  const { tryAgainSubmitHandler, restartSubmitHandler } = handler;
  const isAttemptEnabled =
    quizMetadata.lmsTrackingEnable &&
    quizAttemptResponse?.isAttemptFeatureEnabled;
  const replaceKeywords = (contentBody: string) =>
    contentBody
      .replace(/::(score|SCORE)::/g, `${score}`)
      .replace(/::(pass_mark|PASS_MARK)::/g, `${pass}`);

  const buttonElement = (buttonContent: string, submitHandler: () => void) => (
    <div className="buttonwrapper">
      <Button
        id="ela-fe-quiz-result-try-again"
        type="primary"
        onClick={() => submitHandler()}
      >
        {buttonContent}
      </Button>
    </div>
  );
  const showTryAgain = () => {
    if (content?.retryContent) {
      const isButtonExisting = content.retryContent.includes(
        'ela-fe-quiz-result-try-again',
      );

      return (
        <>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(replaceKeywords(content.retryContent)),
            }}
          />
          {!isButtonExisting &&
            buttonElement('Try Again', tryAgainSubmitHandler)}
        </>
      );
    }

    return (
      <Card heading="PLEASE TRY AGAIN">
        <Paragraph>
          You were unsuccessful in completing this quiz. The pass mark for this
          quiz is {pass}% and you scored {score}%.
        </Paragraph>
        <Paragraph>
          Click the ‘Try Again’ button below to have another attempt at the
          quiz.
        </Paragraph>
        {buttonElement('Try Again', tryAgainSubmitHandler)}
      </Card>
    );
  };

  const showFailMessageBasedOnBehaviour = (failbehaviour: number) => {
    let retryMessage = 'Try again after specified time has elapsed.';
    if (!quizAttemptResponse) return retryMessage;

    const retryTimeInMinutes =
      processRetryTimeInSeconds(
        quizMetadata.retryPeriodValue,
        quizMetadata.retryPeriodType,
      ) / 60;

    const timeDifferenceInMinutes = getTimeDifference(
      quizAttemptResponse.lastQuizAttemptTime,
    );

    if (
      quizAttemptResponse.lastQuizAttemptTime !== null &&
      timeDifferenceInMinutes < retryTimeInMinutes
    ) {
      const remainingTimeInMinutes = Math.trunc(
        retryTimeInMinutes - timeDifferenceInMinutes,
      );
      retryMessage = processRetryTimeMessage(remainingTimeInMinutes);
    }

    switch (failbehaviour) {
      case FailBehaviour.restart:
        return buttonElement('Restart', restartSubmitHandler);
      case FailBehaviour.retry:
        return <p>{retryMessage}</p>;
      default:
        return <div />;
    }
  };

  const showFail = () =>
    content && content.failContent ? (
      <>
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(replaceKeywords(content.failContent)),
          }}
        />
        {isAttemptEnabled &&
          showFailMessageBasedOnBehaviour(quizMetadata.failBehaviour)}
      </>
    ) : (
      <Card heading="Quiz Failed">
        <Paragraph>
          You were unsuccessful in completing this quiz. The pass mark for this
          quiz is {pass}% and you scored {score}%.
        </Paragraph>
        {isAttemptEnabled &&
          showFailMessageBasedOnBehaviour(quizMetadata.failBehaviour)}
      </Card>
    );

  const showPass = () =>
    content && content.passContent ? (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(replaceKeywords(content.passContent)),
        }}
      />
    ) : (
      <Card heading="CONGRATULATIONS">
        <Paragraph>
          {`You have successfully completed your assessment with a score of ${score}%.`}
        </Paragraph>
        <Paragraph>You can now close this window.</Paragraph>
      </Card>
    );

  const attemptLeft = quizAttemptResponse?.remainingQuizAttempt ?? 0;
  const showFailContent = () =>
    attemptLeft <= 0 && isAttemptEnabled ? showFail() : showTryAgain();

  return (
    <div id="QuizResult">
      {score < pass && showFailContent()}
      {score >= pass && showPass()}
    </div>
  );
};

export default QuizResult;
